/*
 * Copyright (c) CIRROM 2021.
 */

import TextField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import CardHeader from "@mui/material/CardHeader";
import Popper from "@mui/material/Popper";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { isEmpty } from "lodash";
import Checkbox from "@mui/material/Checkbox";
import DownloadIcon from "@mui/icons-material/Download";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import {
  CRMDatabaseIDCellRenderer,
  GoogleSearchRenderer,
  LinkedInSearchRenderer,
} from "./CellRenderers";

export const ColumnSelector = React.memo(function ColumnSelector(props: {
  isColumnChooserOpen: boolean;
  popperAnchor: any;
  columnsByColId: any;
  toggleColumnVisibility(column, overrideVisible?): void;
  setColumnChooserOpen(isOpen): void;
}) {
  return (
    <Popper
      open={props.isColumnChooserOpen}
      anchorEl={props.popperAnchor}
      placement={"bottom-start"}
      transition
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={350}>
          <Card style={{ height: "275px", minWidth: "200px" }}>
            <CardHeader subheader={"Column Config"} />
            <Divider />
            <CardContent style={{ height: "200px", overflowY: "scroll" }}>
              <List>
                {props.columnsByColId &&
                  Object.values(props.columnsByColId).map(
                    (column: {
                      name: string;
                      colId: string;
                      visible: boolean;
                    }) => {
                      return (
                        <ListItem
                          key={column.colId}
                          style={{ padding: "0px", margin: "0px" }}
                        >
                          <Checkbox
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                            style={{ padding: "0px", margin: "0px" }}
                            checked={column.visible}
                            onChange={() =>
                              props.toggleColumnVisibility(column)
                            }
                          />
                          <ListItemText
                            secondary={column.name}
                            style={{ padding: "0px", margin: "0px" }}
                          />
                        </ListItem>
                      );
                    }
                  )}
              </List>
            </CardContent>
            <Divider />
            <CardActionArea
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ margin: "2px", padding: "0px 5px" }}
                component={"span"}
                onClick={() => {
                  Object.values(props.columnsByColId).forEach((column) => {
                    props.toggleColumnVisibility(column, false);
                  });
                  props.setColumnChooserOpen(false);
                }}
              >
                Hide All
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ margin: "2px", padding: "0px 5px" }}
                component={"span"}
                onClick={() => {
                  Object.values(props.columnsByColId).forEach((column) => {
                    props.toggleColumnVisibility(column, true);
                  });
                  props.setColumnChooserOpen(false);
                }}
              >
                Show All
              </Button>
            </CardActionArea>
          </Card>
        </Fade>
      )}
    </Popper>
  );
});

/**
 * GridStateToolbar Component
 *
 * This component provides a toolbar for controlling and customizing a data grid. It includes features like quick filtering, adjusting probability range, exporting data, and managing column visibility.
 *
 * @param {Object} props - The component's props.
 * @param {function} props.setQuickFilterValue - A function to set the quick filter value.
 * @param {string} props.quickFilterValue - The current quick filter value.
 * @param {any} props.gridApi - The API reference for the grid.
 * @param {boolean} [props.showExportButton] - Determines whether the export button should be displayed.
 * @param {number} props.rowCount - The number of rows in the grid.
 * @param {any} [props.gridFilterModel] - The filter model for the grid.
 * @param {number[]} [props.defaultProbability] - The default probability range for filtering.
 * @param {boolean} [props.rowDataChanged] - Indicates whether the row data has changed.
 *
 * @returns {JSX.Element} A React component that renders the grid toolbar.
 */
export const GridStateToolbar = React.memo(function GridStatusToolbar(props: {
  setQuickFilterValue(filter: string): void;
  quickFilterValue: string;
  gridApi: any;
  showExportButton?: boolean;
  rowCount: number;
  gridFilterModel?: any;
  defaultProbability?: number[];
  rowDataChanged?: boolean;
}) {
  const {
    setQuickFilterValue,
    gridApi,
    showExportButton,
    rowCount,
    defaultProbability,
  } = props;

  // State and effect hooks
  const [isColumnChooserOpen, setIsColumnChooserOpen] =
    useState<boolean>(false);
  const [popperAnchor, setPopperAnchor] = useState<any>(undefined);
  const [columnsByColId, setColumnsByColId] = useState<{
    colId: { name: string; colId: string; visible: boolean };
  }>(null);
  const [quickFilterTimeout, setQuickFilterTimeout] = useState<any>(null);
  const [gridFilterModel] = useState<any>(props.gridFilterModel);

  const [probabilityValue, setProbabilityValue] = useState<number[]>(
    defaultProbability ?? [75, 100]
  );

  useEffect(() => {
    // Initialize probability value if defaultProbability is provided
    if (defaultProbability) {
      setProbabilityValue(defaultProbability);
    }
  }, [defaultProbability]);

  const [localQuickFilterValue, setLocalQuickFilterValue] = useState<string>(
    props.quickFilterValue
  );

  // Update column list when gridApi changes or when column chooser opens
  useEffect(() => {
    if (gridApi && isColumnChooserOpen) {
      updateColumnsList();
    }
  }, [gridApi, isColumnChooserOpen]);

  useEffect(() => {
    // Update localQuickFilterValue when props.quickFilterValue changes
    setLocalQuickFilterValue(props.quickFilterValue);
  }, [props.quickFilterValue]);

  useEffect(() => {
    let isMounted = true;
    let timeout = null;
    if (gridApi?.getColumnDef("probability")) {
      timeout = setTimeout(() => {
        if (probabilityValue && isMounted) {
          if (probabilityValue[0] === 0 && probabilityValue[1] === 0) {
            gridApi.setFilterModel({
              ...gridApi.getFilterModel(),
              probability: {
                filterType: "number",
                operator: "OR",
                conditions: [
                  {
                    type: "equals",
                    filter: 0,
                  },
                  {
                    type: "blank",
                    filter: probabilityValue[1],
                  },
                ],
              },
            });
          } else {
            gridApi.setFilterModel({
              ...gridApi.getFilterModel(),
              probability: {
                filterType: "number",
                operator: "AND",
                conditions: [
                  {
                    type: "greaterThanOrEqual",
                    filter: probabilityValue[0],
                  },
                  {
                    type: "lessThanOrEqual",
                    filter: probabilityValue[1],
                  },
                ],
              },
            });
          }

          if (probabilityValue[0] === 0 && probabilityValue[1] === 100) {
            const existingFilters = gridApi.getFilterModel();
            delete existingFilters.probability;

            gridApi.setFilterModel(existingFilters);
          }
        } else {
          gridApi.destroyFilter("probability");
        }
      }, 250);
    }
    return () => {
      isMounted = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [gridApi, probabilityValue, gridFilterModel, props.rowDataChanged]);

  const updateColumnsList = () => {
    if (!gridApi) return;

    let incomingColumns: any = {};
    const allColumns = gridApi.getColumns();
    if (allColumns && allColumns.length > 0) {
      allColumns.forEach((column) => {
        const colId = column.getColId();
        const colDef = column.getColDef();
        incomingColumns[colId] = {
          colId: colId,
          name: colDef.headerName || colId,
          visible: column.isVisible(),
        };
      });
    }

    if (!isEmpty(incomingColumns)) {
      setColumnsByColId(incomingColumns);
    }
  };

  /**
   * toggleColumnVisibility Function
   *
   * This function toggles the visibility of a column in the grid based on the provided column object and optional overrideVisible flag.
   *
   * @param {Object} column - The column object to toggle visibility for.
   * @param {boolean} [overrideVisible] - An optional flag to override the visibility state.
   */
  const toggleColumnVisibility = (column, overrideVisible?) => {
    let visible = !column.visible;
    if (overrideVisible !== undefined) {
      visible = overrideVisible;
    }

    // In AG Grid v33, use setColumnsVisible with an array of column keys
    gridApi.setColumnsVisible([column.colId], visible);
    gridApi.autoSizeColumns([column.colId]);
    setColumnsByColId({
      ...columnsByColId,
      [column.colId]: {
        name: column.name,
        colId: column.colId,
        visible: visible,
      },
    });
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "self-end",
        alignContent: "space-between",
        justifyContent: "space-between",
        margin: "10px",
      }}
    >
      <TextField
        name={"quick_filter"}
        size={"small"}
        label={"Filter..."}
        style={{ margin: "0px", padding: "0px" }}
        value={localQuickFilterValue || ""}
        onChange={(event) => {
          setLocalQuickFilterValue(event.target.value);
          if (quickFilterTimeout) {
            clearTimeout(quickFilterTimeout);
          }
          setQuickFilterTimeout(
            setTimeout(() => {
              setQuickFilterValue(event.target.value);
            }, 250)
          );
        }}
        variant={"standard"}
      />

      <div style={{ width: "25%" }}>
        <Typography variant={"caption"}>
          {`Probability Range:  >=${probabilityValue[0]}% & <=${probabilityValue[1]}%`}
        </Typography>
        <Slider
          style={{ padding: "0px" }}
          size="small"
          value={probabilityValue}
          valueLabelDisplay="auto"
          onChange={(_event, newValue) =>
            setProbabilityValue(newValue as number[])
          }
        />
      </div>

      <div style={{ display: "flex", alignItems: "center" }}>
        <Typography variant={"caption"}>
          Rows:{new Intl.NumberFormat("en-US").format(rowCount)}
        </Typography>
        {!isEmpty(gridApi) && showExportButton && (
          <IconButton
            color={"info"}
            component={"span"}
            onClick={() => {
              gridApi.exportDataAsCsv({
                processCellCallback: (cell) => {
                  if (
                    cell.column.colDef.cellRenderer === "googleSearchRenderer"
                  ) {
                    return GoogleSearchRenderer(cell.node).props.children;
                  }
                  if (
                    cell.column.colDef.cellRenderer === "linkedinSearchRenderer"
                  ) {
                    return LinkedInSearchRenderer(cell.node).props.children;
                  }
                  if (
                    cell.column.colDef.cellRenderer === "crmDatabaseIdRenderer"
                  ) {
                    return CRMDatabaseIDCellRenderer(cell.node);
                  }
                  return cell.value;
                },
              });
            }}
          >
            <DownloadIcon />
          </IconButton>
        )}
        <IconButton
          color={"info"}
          aria-label={"column visibility"}
          component={"span"}
          onClick={(event) => {
            setPopperAnchor(event.currentTarget);
            setIsColumnChooserOpen(!isColumnChooserOpen);
          }}
        >
          <ViewColumnOutlinedIcon />
        </IconButton>
      </div>

      <ColumnSelector
        isColumnChooserOpen={isColumnChooserOpen}
        popperAnchor={popperAnchor}
        columnsByColId={columnsByColId}
        toggleColumnVisibility={toggleColumnVisibility}
        setColumnChooserOpen={setIsColumnChooserOpen}
      />
    </div>
  );
});
